import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  mapAclPermissionsResponseToDto,
  mapAclRolePayloadFromForm,
  mapAclRolesResponseToDto,
} from '../dto/system-acl.dto.js';
import { buildAclRoleForm, buildPermissionGroups, getRoleById, validateAclRoleForm } from '../model/system-acl.model.js';
import { buildSystemAclListPath, getSystemAclEditorMode } from '../model/system-acl-routes.js';

export function useSystemAclRoleEditor() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const editorState = useMemo(() => getSystemAclEditorMode(params), [params]);

  const [permissions, setPermissions] = useState([]);
  const [form, setForm] = useState(() => buildAclRoleForm());
  const [formErrors, setFormErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [roleNotFound, setRoleNotFound] = useState(false);

  const permissionGroups = useMemo(() => buildPermissionGroups(permissions), [permissions]);
  const isEditing = editorState.mode === 'edit';

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setRoleNotFound(false);

    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        adminApi.getAclRoles(token),
        adminApi.getAclPermissions(token),
      ]);

      const nextRoles = mapAclRolesResponseToDto(rolesResponse);
      const nextPermissions = mapAclPermissionsResponseToDto(permissionsResponse);

      setPermissions(nextPermissions);

      if (isEditing) {
        const role = getRoleById(nextRoles, editorState.roleId);
        if (!role) {
          setForm(buildAclRoleForm());
          setRoleNotFound(true);
          return;
        }

        setForm(buildAclRoleForm(role));
      } else {
        setForm(buildAclRoleForm());
      }

      setFormErrors({});
    } catch (requestError) {
      setPermissions([]);
      setForm(buildAclRoleForm());
      setError(requestError?.message || 'Failed to load access control data');
    } finally {
      setIsLoading(false);
    }
  }, [editorState.roleId, isEditing, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onFormChange = useCallback((key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFormErrors((current) => {
      if (!current[key]) {
        return current;
      }

      const next = { ...current };
      delete next[key];
      return next;
    });
  }, []);

  const togglePermissionKey = useCallback((permissionKey) => {
    setForm((current) => {
      const currentKeys = Array.isArray(current.permissionKeys) ? current.permissionKeys : [];
      return {
        ...current,
        permissionKeys: currentKeys.includes(permissionKey)
          ? currentKeys.filter((item) => item !== permissionKey)
          : [...currentKeys, permissionKey].sort(),
      };
    });
  }, []);

  const cancel = useCallback(() => {
    navigate(buildSystemAclListPath());
  }, [navigate]);

  const saveRole = useCallback(async (event) => {
    event.preventDefault();

    const nextErrors = validateAclRoleForm(form);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      return;
    }

    if (roleNotFound) {
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const payload = mapAclRolePayloadFromForm(form);

      if (isEditing) {
        await adminApi.updateAclRole(token, editorState.roleId, {
          name: payload.name,
          description: payload.description,
          permissions: payload.permissions,
        });
      } else {
        await adminApi.createAclRole(token, payload);
      }

      navigate(buildSystemAclListPath(), {
        replace: true,
        state: {
          success: isEditing ? 'Role updated.' : 'Role created.',
        },
      });
    } catch (requestError) {
      setError(requestError?.message || 'Failed to save role');
    } finally {
      setIsSaving(false);
    }
  }, [editorState.roleId, form, isEditing, navigate, roleNotFound, token]);

  return {
    error,
    form,
    formErrors,
    isEditing,
    isLoading,
    isSaving,
    permissionGroups,
    roleNotFound,
    cancel,
    onFormChange,
    saveRole,
    togglePermissionKey,
  };
}
